<!--赖传峰-->
<!--2022/2/9-->
<script
  lang="ts"
  setup
>
import { useCookie } from '#imports'
import { EVipRegionType } from '@/api'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import {
  RoutePathOfDrive,
  StorageKey,
} from '@/types/enum'
import { xBase } from '@/utils/xbase'
import { ElMessageBox } from 'element-plus'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import QuestionCircle from '~/components/icons/QuestionCircle.vue'
import { AvatarSize } from './avatar.script'
import Avatar from './Avatar.vue'

withDefaults(defineProps<{
  showUserNav?: boolean
}>(), {})

// eslint-disable-next-line no-spaced-func
const emit = defineEmits<{
  (e: 'after-logout'): void
}>()

const userInfo = useUserInfo()
const layoutStore = useLayout()
const { t } = useI18n()

const vipExpires = computed(() => (userInfo.premium ? userInfo.getVipDurationDays() : 0))
const regionalVipDays = computed(() => userInfo.getVipDurationDays(EVipRegionType.REGIONAL))
const globalVipDays = computed(() => userInfo.getVipDurationDays(EVipRegionType.GLOBAL))

const logout = async () => {
  ElMessageBox.confirm(
    t('logout-confirm'),
    t('logout'),
    {
      cancelButtonClass: 'el-button--text logout-cancel-button',
      confirmButtonText: t('confirm'),
      cancelButtonText: t('cancel'),
      customClass: 'el-message-box-cover',
    },
  ).then(async () => {
    await (await xBase)?.logout()
    useCookie(StorageKey.USER_ID).value = null
    useCookie(StorageKey.IS_NEW_USER).value = null
    userInfo.$reset()
    emit('after-logout')
  }).catch(console.error)
}

defineExpose({
  logout,
})

</script>

<template>
  <div
    :class="[userInfo.premium && 'premium', layoutStore.sidebarMobileVisible && 'sidebar']"
    class="flex-center user-info"
  >
    <div class="flex-center">
      <Avatar
        :premium="userInfo.premium"
        :size="AvatarSize.LARGE"
        :src="userInfo.avatar"
      />
      <div class="info">
        <div
          class="flex-center"
          style="justify-content: left;"
        >
          <div
            :title="userInfo.username"
            class="name ellipsis"
          >
            {{ userInfo.username }}
          </div>
          <div
            v-if="userInfo.premium"
            class="badge"
          >
            Premium
          </div>
        </div>
        <div
          :class="userInfo.phone && userInfo.email && 'all'"
          class="phone_email"
        >
          <div
            v-if="userInfo.phone"
            :title="userInfo.phone"
          >
            {{ userInfo.phone }}
          </div>
          <div class="divider" />
          <div
            v-if="userInfo.email"
            :title="userInfo.email"
          >
            {{ userInfo.email }}
          </div>
        </div>
        <div
          v-if="vipExpires"
          class="vip-expires"
        >
          <span class="">{{ $t('hAb15maCmfRgJqEEarMy', [vipExpires]) }}</span>
          <ElTooltip
            v-if="userInfo.hasTwoTypeVip"
            popper-class="vip-expires-tooltip"
          >
            <template #content>
              <p>{{ $t('global-vip-days', [globalVipDays]) }}</p>
              <p>{{ $t('regional-vip-days', [regionalVipDays]) }}</p>
            </template>
            <QuestionCircle />
          </ElTooltip>
        </div>
      </div>
    </div>
  </div>
  <ul
    v-if="!layoutStore.sidebarMobileVisible || showUserNav"
    class="user-nav"
  >
    <li>
      <a
        :href="RoutePathOfDrive.ACCOUNT"
        class="pp-link-button"
      >
        {{ $t('account-page-title') }}
      </a>
    </li>
    <li @click.prevent="logout">
      <a
        class="pp-link-button"
        href="javascript:void(0);"
      >
        {{ $t('logout') }}
      </a>
    </li>
  </ul>
</template>
<style
  lang="less"
  scoped
>
@import 'assets/styles/util.less';

li {
  border-radius: 4px;
  padding: 0 16px;

  a {
    height: 48px;
    font-size: 14px;
    justify-content: flex-start;
  }

  &:hover {
    background: var(--color-hover);
  }

  &.active {
    background: var(--color-active);
  }
}

.ellipsis {
  #ellipsis();
}

.user-info {
  --width: 320px;
  justify-content: space-between;
  height: 80px;
  padding: 16px;
  background: linear-gradient(92.41deg, #eef8ff 6.75%, #dbeffc 106.52%);

  &.premium {
    background: linear-gradient(93.6deg, #fff9ec 6.61%, #fff4de 102.6%);

    .name {

      color: #674624;
      max-width: calc(var(--width) - 94px - 68px);
    }

    .phone_email {
      color: rgba(103, 70, 36, 0.6);
    }

    .divider {
      background: rgba(103, 70, 36, 0.3);
    }
  }
}

.info {
  margin-left: 12px;

  > div {
    line-height: 22px;
  }
}

.name {
  font-weight: 600;
  font-size: 16px;
  max-width: calc(var(--width) - 94px);
  color: var(--color-primary-text);

}

.badge {
  margin-left: 8px;
  border: 0.5px solid rgba(103, 70, 36, 0.5);
  border-radius: 3px;
  line-height: 16px;
  font-size: 10px;
  width: 60px;
  padding: 0 4px;
  text-align: center;
  color: #674624;
  white-space: nowrap;
}

.phone_email {
  display: flex;
  color: var(--color-secondary-text);

  .divider {
    display: none;
  }

  &.all {
    > div {
      #ellipsis();
      max-width: calc((var(--width) - 94px - 11px) / 2);
    }

    .divider {
      display: block;
      margin: 6px 5px;
      width: 1px;
      height: 10px;
      background: rgba(171, 171, 171, 0.6);
    }
  }
}

.user-nav {
  padding: 16px;

  a {
    color: var(--color-primary-text) !important;
  }
}

.vip-expires {
  display: none;
  color: rgba(103, 70, 36, 0.6);

  > span {
    vertical-align: middle;
  }

  > svg {
    width: 14px;
    height: 14px;
    vertical-align: middle;
    margin-left: 4px;
  }
}

@media screen and (max-width: @mobile-break-width) {
  .user-info {
    --width: 290px;

    &.sidebar {
      margin: -8px -16px 0;
      background: #fff;
    }
  }
}

@media screen and (max-width: 460px) {
  .vip-expires {
    display: block;
  }
}
</style>
